import type { NavigationSection } from '@/contexts/NavigationContext/NavigationContext';
import type { ISessionUser } from '@/interfaces';
import { BrowserHttpClient } from '@/services/Http/BrowserHttpClient';
import SessionStorage from '@/services/SessionStorage';
import { logger } from '@/utils/logger/logger';
import type { Context } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import type { FCh } from 'react-fc';

export const NOTIFICATION_CACHE_KEY = ['notifications', 'badge'];

export interface ISessionData {
    user?: ISessionUser;
    navLinks: NavigationSection[];
    notificationBadge?: NotificationBadge;
    announcement?: {
        path: string;
        message: string;
    };
    requireDetailConfirmation: boolean;
    previewMode?: boolean;
    userInChina?: boolean;
    isOfflineSession?: boolean;
}

type NotificationBadge = {
    totalCount: number;
    myItemsCombinedCount: number;
    savedItemCount: number;
    exportCount: number;
    whatsNewCount: number;
};

export type FeatureEnablement = {
    canAccessCleanEnergyExposureScore: boolean;
};

export interface IAppContextData extends ISessionData {
    featureEnablement: FeatureEnablement;
    hasFeature: (feature: string) => boolean;
}

const emptySessionData: IAppContextData = {
    user: undefined,
    notificationBadge: undefined,
    navLinks: undefined,
    requireDetailConfirmation: false,
    isOfflineSession: false,
    featureEnablement: {
        canAccessCleanEnergyExposureScore: true,
    },
    hasFeature: () => false,
};
export const AppContext: Context<IAppContextData> = createContext(emptySessionData);

export const AppContextProvider: FCh<IProps> = ({ initialSession, children }) => {
    const { user, navLinks, announcement, requireDetailConfirmation, previewMode, userInChina, isOfflineSession } =
        initialSession;
    const [notificationBadge, setNotificationBadge] = useState<NotificationBadge>();

    const getNotifications = async () => {
        if (!user || user.isGuestUser) {
            return;
        }
        try {
            const cachedData = SessionStorage.get(NOTIFICATION_CACHE_KEY);
            if (cachedData) {
                setNotificationBadge(cachedData);
                return;
            }

            const data = await new BrowserHttpClient().get('/users/notifications');
            if (data) {
                SessionStorage.set(NOTIFICATION_CACHE_KEY, data.notificationBadge);
                setNotificationBadge(data.notificationBadge);
            }
        } catch (error) {
            logger.error('Error reading notification cache:', error);
        }
    };

    useEffect(() => {
        getNotifications();
    }, []);

    return (
        <AppContext.Provider
            value={{
                user,
                navLinks,
                announcement,
                notificationBadge,
                requireDetailConfirmation,
                previewMode,
                userInChina,
                isOfflineSession,
                featureEnablement: {
                    canAccessCleanEnergyExposureScore: !user?.regulationRestrictions?.includes('SEBI'),
                },
                hasFeature: (feature: string): boolean => {
                    return Boolean(user?.enabledFeatures?.includes(feature));
                },
            }}
        >
            {children}
        </AppContext.Provider>
    );
};

export const useAppContext = (): IAppContextData => {
    return useContext(AppContext); // once everything is within a context add back the "throw" if not set
};

interface IProps {
    initialSession: ISessionData;
}
